import type { z } from 'zod';
import { ApiError, codeFromStatus, errorEnvelopeSchema } from './apiError';
import { http } from './http';
import type { ListQuery } from './list';
import { parseApiResponse } from './parse';

/**
 * Страница реестра админки (ТЗ API-07): `GET url?page&page_size&sort&q&filter[…]` → `{items, total,
 * can}`; ответ проверяется сгенерированной схемой операции (`zXxxResponse`).
 */
export async function fetchList<T>(
  url: string,
  query: ListQuery,
  schema: z.ZodType<T>,
): Promise<T> {
  const response = await http.get<unknown>(url, { params: query });
  return parseApiResponse(schema, response.data, url);
}

/** Имя файла из `Content-Disposition` (`filename*=UTF-8''…` или `filename="…"`). */
export function fileNameFromDisposition(header: unknown, fallback: string): string {
  if (typeof header !== 'string') return fallback;
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      return fallback;
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header)?.[1];
  return plain?.trim() || fallback;
}

/**
 * Выгрузка CSV текущей выборки реестра (`GET …/export` с теми же sort/q/filter): файл — Blob,
 * имя — из `Content-Disposition` сервера (иначе `fallbackName`).
 */
export async function fetchExport(
  url: string,
  query: Omit<ListQuery, 'page' | 'page_size'>,
  fallbackName: string,
): Promise<{ blob: Blob; fileName: string }> {
  // Ошибка приходит тем же Blob (например, 422 export_too_large) — разбираем конверт сами;
  // 401 идёт обычным путём интерсептора (обновление токена и повтор).
  const response = await http.get<Blob>(url, {
    params: query,
    responseType: 'blob',
    validateStatus: (status) => status !== 401,
  });
  if (response.status >= 400) throw await blobError(response.status, response.data);
  return {
    blob: response.data,
    fileName: fileNameFromDisposition(response.headers['content-disposition'], fallbackName),
  };
}

async function blobError(status: number, data: unknown): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = data instanceof Blob ? JSON.parse(await data.text()) : data;
  } catch {
    body = null;
  }
  const envelope = errorEnvelopeSchema.safeParse(body);
  if (envelope.success) {
    return new ApiError({
      code: envelope.data.error.code,
      message: envelope.data.error.message,
      details: envelope.data.error.details,
      status,
      requestId: envelope.data.error.trace_id ?? null,
    });
  }
  return new ApiError({ code: codeFromStatus(status), message: `HTTP ${status}`, status });
}
